/**
 * effects.js
 * Lightweight UI effects for damage feedback (screen shake, area flash)
 */


import { FX } from './fx.js';
import { SoundEffects } from './sound-effects.js';
let Effects = {};

Effects = (function() {
    function reducedMotion() {
        try {
            return typeof window.matchMedia === 'function'
                && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        } catch (_) {
            return false;
        }
    }

    function spawnFallbackOverlay(className, durationMs) {
        let layer = document.getElementById('fx-layer');
        if (!layer) {
            layer = document.createElement('div');
            layer.id = 'fx-layer';
            layer.setAttribute('aria-hidden', 'true');
            document.body.appendChild(layer);
        }
        const el = document.createElement('div');
        el.className = `${className} fx-overlay-fallback`;
        layer.appendChild(el);
        setTimeout(() => { if (el.isConnected) el.remove(); }, durationMs + 400);
        return el;
    }

    function withTempClass(el, className, durationMs) {
        if (!el) return;
        try {
            el.classList.remove(className);
            // eslint-disable-next-line no-unused-expressions
            el.offsetHeight;
            el.classList.add(className);
            setTimeout(() => el.classList.remove(className), durationMs);
        } catch (_) {}
    }

    // screenShake/portraitShake delegate to FX (WAAPI, overlap-safe,
    // damage-scaled). If fx.js is unavailable, feedback still lives on the
    // transient overlay: never transform a card, portrait, or #game-view.
    function screenShake(damageAmount = 0, durationMs = 450) {
        if (FX?.screenShake) {
            FX.screenShake(damageAmount);
            return;
        }
        if (typeof damageAmount === 'number' && damageAmount <= 3) return;
        if (reducedMotion() || !document.getElementById('game-view')) return;
        const amp = Math.max(4, Math.min(22, 2 + (damageAmount || 0) * 1.4));
        const overlay = spawnFallbackOverlay('fx-screen-impact', durationMs);
        overlay.style.setProperty('--fx-impact-amp', `${amp}px`);
        overlay.style.setProperty('--fx-impact-amp-neg', `${-amp}px`);
        overlay.style.setProperty('--fx-impact-half', `${amp * 0.5}px`);
        overlay.style.setProperty('--fx-impact-half-neg', `${-amp * 0.5}px`);
        overlay.style.setProperty('--fx-duration', `${durationMs}ms`);
    }

    function portraitShake(target = 'opponent', amount = 5, durationMs = 500) {
        if (FX?.portraitShake) {
            FX.portraitShake(target, amount);
            return;
        }
        // Skip negligible hits (<= 1); allow 2..3 for light shakes
        if (typeof amount === 'number' && amount <= 1) return;
        let el = null;
        if (target === 'player') el = document.getElementById('player-portrait');
        else if (target === 'opponent') el = document.getElementById('opponent-portrait');
        else if (target instanceof HTMLElement) el = target;
        if (!el) return;
        if (reducedMotion()) return;
        const rect = el.getBoundingClientRect?.();
        if (!rect) return;
        const amp = Math.max(4, Math.min(16, 2 + (amount || 0) * 0.9));
        const overlay = spawnFallbackOverlay('fx-portrait-impact', durationMs);
        overlay.style.left = `${rect.left + rect.width / 2}px`;
        overlay.style.top = `${rect.top + rect.height / 2}px`;
        overlay.style.width = `${rect.width}px`;
        overlay.style.height = `${rect.height}px`;
        overlay.style.setProperty('--fx-portrait-amp', `${amp}px`);
        overlay.style.setProperty('--fx-portrait-amp-neg', `${-amp}px`);
        overlay.style.setProperty('--fx-portrait-half', `${amp * 0.5}px`);
        overlay.style.setProperty('--fx-portrait-half-neg', `${-amp * 0.5}px`);
        overlay.style.setProperty('--fx-duration', `${durationMs}ms`);
    }

    // Restart-safe timers for the view-transition fade.
    const viewEnterTimers = new WeakMap();

    // Shared view-transition fade: entering views get a short one-shot
    // fade-in instead of popping. Display toggles stay the source of truth;
    // the class self-cleans and re-entry restarts the fade.
    function viewEnter(el) {
        if (!el) return;
        const prev = viewEnterTimers.get(el);
        if (prev) clearTimeout(prev);
        try {
            el.classList.remove('view-enter');
            // eslint-disable-next-line no-unused-expressions
            el.offsetHeight;
            el.classList.add('view-enter');
            viewEnterTimers.set(el, setTimeout(() => {
                el.classList.remove('view-enter');
                viewEnterTimers.delete(el);
            }, 260));
        } catch (_) {}
    }

    // Sound is debounced so mashing an unplayable card doesn't machine-gun
    // the error thud; the shake still replays per attempt.
    let lastErrorSoundAt = 0;

    function invalidAction({ cardElement = null } = {}) {
        const now = Date.now();
        if (now - lastErrorSoundAt >= 1000 || lastErrorSoundAt === 0) {
            lastErrorSoundAt = now;
            SoundEffects?.play('error');
        }
        if (cardElement) withTempClass(cardElement, 'invalid-shake', 380);
    }

    return {
        screenShake,
        portraitShake,
        invalidAction,
        viewEnter,
        withTempClass
    };
})();

export { Effects };
